/**
 * ชนิดข้อมูลของ Supplier Master — เขียนตาม payload contract ที่ freeze แล้ว
 * (`docs/API_CONTRACT.md` — "Supplier Master — Payload Contract (D-083)")
 *
 * Supplier เป็น master **ระดับองค์กร** ไม่มี `plant_id` และไม่มีมุมมองรายโรงงาน
 * — ต่างจาก Product Master ที่มี `plant_data` ติดมาด้วยทุกใบ นั่นคือความต่างข้อแรก
 * ที่คนอ่านทั้งสองไฟล์ควรเห็น และเป็นเหตุผลที่หน้าจอของมันไม่มีตัวเลือกโรงงานเลย
 *
 * `supplier_code` เป็น **business key ที่แก้ไม่ได้** (D-083): ใบสั่งซื้อ การตรวจรับ
 * และ lot ที่อ้างถึงมันต้อง trace ได้ต่อไปตลอด รหัสจึงถูกเขียนครั้งเดียวตอนสร้าง
 * และไม่เคยเดินทางไปกับ `PATCH` อีกเลย — ดู `UpdateSupplierBody` ด้านล่างซึ่ง
 * **ไม่มีคีย์นั้นอยู่ในชนิดข้อมูล** ไม่ใช่แค่ไม่ส่ง
 *
 * **ไม่มี DELETE โดยตั้งใจ** เลิกใช้ผู้ขายคือ `PATCH status = INACTIVE` ไม่ใช่การลบ
 * แถวออกจากใต้เอกสารที่อ้างมันอยู่
 */

/** รหัสโมดูลของหน้าจอนี้ใน RBAC matrix — โมดูล **ธุรกิจ** ปกติ ADMIN ถูก deny (D-060) */
export const SUPPLIER_MASTER_MODULE = 'SUPPLIER_MASTER';

/* ------------------------------------------------------------- สถานะ ----- */

export const ACTIVE_SUPPLIER_STATUS = 'ACTIVE';
export const INACTIVE_SUPPLIER_STATUS = 'INACTIVE';
export const BLOCKED_SUPPLIER_STATUS = 'BLOCKED';

/**
 * สามค่าที่ CHECK constraint ฝั่งเซิร์ฟเวอร์รับ ตามลำดับที่ contract เขียนไว้
 *
 * `BLOCKED` **ไม่ใช่คำพ้องของ `INACTIVE`**: ผู้ขายที่ inactive คือความสัมพันธ์ที่จบ
 * ไปแล้ว ส่วนผู้ขายที่ blocked คือความสัมพันธ์ที่ยังอยู่แต่ถูกระงับไว้ การห้ามออก PO
 * ใหม่ให้ผู้ขาย `BLOCKED` ถูกบังคับที่ P2-2 (Procurement) ไม่ใช่ที่นี่
 */
export const SUPPLIER_STATUSES = [
  ACTIVE_SUPPLIER_STATUS,
  INACTIVE_SUPPLIER_STATUS,
  BLOCKED_SUPPLIER_STATUS,
] as const;

export type SupplierStatus = (typeof SUPPLIER_STATUSES)[number];

/** ค่าที่เซิร์ฟเวอร์เติมให้เมื่อ `POST` ไม่ได้ส่ง `status` มา */
export const DEFAULT_SUPPLIER_STATUS = ACTIVE_SUPPLIER_STATUS;

export function isSupplierStatus(value: string): value is SupplierStatus {
  return (SUPPLIER_STATUSES as readonly string[]).includes(value);
}

/* -------------------------------------------------------- ข้อจำกัดความยาว -- */

/** `supplier_code` ≤ 20 และ `supplier_name` ≤ 200 — ตรงกับคอลัมน์จริงฝั่งเซิร์ฟเวอร์ */
export const SUPPLIER_CODE_MAX_LENGTH = 20;
export const SUPPLIER_NAME_MAX_LENGTH = 200;

/* ------------------------------------------------------------- กฎธุรกิจ --- */

/**
 * `409` เมื่อรหัสผู้ขายนั้นมีเจ้าของอยู่แล้ว — **ไม่มีอะไรถูกเขียนเลย**
 *
 * เป็นคนละเรื่องกับความล้มเหลวของระบบ: ทางแก้อยู่ในมือผู้ใช้ทันที (ใช้รหัสอื่น
 * หรือไปแก้ผู้ขายรายที่ถือรหัสนี้อยู่แล้ว) การยุบมันเข้ากับ `server-error` คือการ
 * ส่งเขาไปโทรหาฝ่ายไอที
 */
export const SUPPLIER_CODE_TAKEN = 'SUPPLIER_CODE_TAKEN';

/**
 * `400` เมื่อ body ของ `PATCH` มี `supplier_code` ติดไปด้วย
 *
 * หน้าจอนี้ไม่มีเส้นทางที่ประกอบคีย์นั้นเข้า body ได้เลย (ดู `UpdateSupplierBody`)
 * การที่มันเกิดขึ้นจริงจึงแปลว่าหน้าจอกับ API ไม่ตรงกันแล้ว — เป็นบั๊กที่ต้องเห็น
 * ไม่ใช่ความล้มเหลวที่กลืนเงียบ ๆ
 */
export const SUPPLIER_CODE_IS_IMMUTABLE = 'SUPPLIER_CODE_IS_IMMUTABLE';

/**
 * `400` เมื่อ body มี `supplier_id`/`created_at`/`updated_at` ซึ่งเซิร์ฟเวอร์เป็น
 * เจ้าของ — ด้วยเหตุผลเดียวกับข้อข้างบน ที่นี่ไม่มีทางประกอบมันขึ้นมาได้
 */
export const SUPPLIER_FIELD_IS_SERVER_OWNED = 'SUPPLIER_FIELD_IS_SERVER_OWNED';

/* ------------------------------------------------------------- resource --- */

/** หนึ่งผู้ขายตามที่ contract ระบุ — หกฟิลด์ ไม่มี `plant_data` */
export interface Supplier {
  /** UUID surrogate ที่เซิร์ฟเวอร์สร้าง (D-039) — ใช้เป็น path ของ `GET`/`PATCH` */
  readonly supplier_id: string;
  readonly supplier_code: string;
  readonly supplier_name: string;
  readonly status: string;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `GET /v1/suppliers` — envelope มาตรฐานของทุกกลุ่ม */
export interface SupplierListPage {
  readonly items: readonly Supplier[];
  readonly next_cursor: string | null;
}

/* -------------------------------------------------------------- requests -- */

/**
 * body ของ `POST /v1/suppliers`
 *
 * `status` เป็น optional จริง ๆ ตาม contract — **ไม่ส่งเลย** ต่างจากส่ง `null`
 * หรือสตริงว่าง ซึ่ง `extra = "forbid"` + enum จะปฏิเสธด้วย `400`
 */
export interface CreateSupplierBody {
  readonly supplier_code: string;
  readonly supplier_name: string;
  readonly status?: string;
}

/**
 * body ของ `PATCH /v1/suppliers/{supplier_id}` — **ไม่มี `supplier_code`**
 *
 * ทั้งสองคีย์เป็น optional เพราะ `PATCH` พาไปเฉพาะสิ่งที่เปลี่ยน คีย์ที่ไม่ได้ส่ง
 * ถูกปล่อยไว้ตามเดิม (เซิร์ฟเวอร์อ่านจาก `model_fields_set` ไม่ใช่จากค่า `null`)
 */
export interface UpdateSupplierBody {
  readonly supplier_name?: string;
  readonly status?: string;
}
